import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {TextField} from '@mui/material';
import dayjs, {Dayjs} from 'dayjs';
import cn from 'classnames';

import {isRequiredValidator} from 'utils/helpers/validators';

import './styles.scss';

interface IInputDate {
  className?: string
  label?: string
  value?: Date
  placeholder?: string
  required?: boolean
  onChange?: (value: Date) => void
  onBlur?: (value: Date) => void
  locale?: string
}

const inputErrorTypes = {
  required: 'required'
};

const errorMessage: { [type: string]: { id: string; defaultMessage: string } } = {
  [inputErrorTypes.required]: {
    id: 'gritx.common.errors.required',
    defaultMessage: 'This field cannot be blank'
  }
};

function validateValue(value: string, required?: boolean): string[] {
  let errors = [] as string[];

  if (required && !isRequiredValidator(value)) {
    errors = [inputErrorTypes.required];
  }

  if (!required && value.length === 0) {
    errors = [];
  }

  return errors;
}

export const InputDate: React.FunctionComponent<IInputDate> = ({
  label,
  required = false,
  onChange,
  onBlur,
  className = '',
  placeholder,
  value,
  locale,
  ...props
}: IInputDate) => {
  const [errors, setErrors] = useState<string[]>([]);

  function handleChange(date: Dayjs | null) {
    setErrors([]);
    if (onChange && date) {
      onChange(date.toDate());
    }
  }

  return <div className={`input-date ${className}`}>
    <label className={'input-date__label'}>
      <span className={'input-date__label-text'}>
        {label}
        {required && <span className={'input-date__required'}>*</span>}
      </span>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        <DatePicker
          disabled={false}
          views={['year', 'month', 'day']}
          openTo="day"
          value={value}
          onChange={(newValue: Dayjs | null) => {
            handleChange(newValue);
          }}
          InputProps={{
            className: cn('input-date__field'),
            onBlur: (e) => {
              setErrors(validateValue(e.target.value, required));
            }
          }}
          renderInput={(params) => <TextField {...params}/>}
        />
      </LocalizationProvider>
    </label>
    {
      errors.map((e, i) => <span key={i} className="input-date__error">
        {errorMessage[e] && <FormattedMessage {...errorMessage[e]}/>}
      </span>)
    }
  </div>;
};
